/**
 * Ingestion Service
 * 
 * Unified service for capturing anything into Swirl:
 * - Chrome extension clips
 * - Drag & drop files
 * - Notes
 * - Links
 * - Images
 */

import { supabase } from '../lib/supabase';
import { prepareItemForInsertion, normalizeChannel, normalizeContentType, normalizeSourceTable } from '../utils/ingestionNormalizers';

class IngestionService {
  /**
   * Capture an item - centralized item creation with automatic event tracking
   * 
   * This is the ONLY way to create items in deepsearch.items.
   * It ensures every item is tracked in public.ingestion_events.
   * 
   * @param {string} userId - User ID
   * @param {object} itemData - Item data (source_type, source_table, url, title, etc.)
   * @param {object} meta - Additional metadata for the ingestion event
   * @returns {object} The created item
   */
  async captureItem(userId, itemData, meta = {}) {
    try {
      // Ensure required fields are present
      if (!itemData.source_type) {
        throw new Error('source_type is required');
      }
      
      // Stage 2: Normalize source_table (default to file_upload for backward compat)
      const rawSourceTable = itemData.source_table || source_table || 'file_upload';
      
      // Stage 2: Normalize and enforce meta.channel, meta.content_type, and source_table
      const normalizedItem = prepareItemForInsertion(itemData, rawSourceTable);
      
      // 1. Create item in deepsearch.items
      // Note: supabase client is configured with schema: 'deepsearch', so .from('items') points to deepsearch.items
      const { data: item, error: itemError} = await supabase
        .from('items')
        .insert({
          user_id: userId,
          ...normalizedItem // Includes normalized source_table and meta with channel/content_type
        })
        .select()
        .single();

      if (itemError) throw itemError;

      // 2. Track ingestion event in public.ingestion_events
      // ✅ IMPORTANT: ingestion_events is in public schema, not deepsearch
      const { error: eventError } = await supabase
        .schema('public')
        .from('ingestion_events')
        .insert({
          user_id: userId,
          source_type: 'item',
          item_id: item.id,
          meta: {
            ...meta,
            item_source_type: itemData.source_type,
            source_table: source_table
          }
        });

      if (eventError) {
        console.error('Failed to track ingestion event:', eventError);
        // Don't throw - item was created successfully
      }

      // 3. CRITICAL: Queue item for worker processing (embeddings, AI analysis)
      // This ensures ALL items get processed, not just URL items via create_url_item RPC
      try {
        // Determine provider based on source type
        const provider = this.getProviderForSourceType(itemData.source_type);
        
        // Queue the job using RPC (this handles user_id from auth context)
        const { data: jobData, error: jobError } = await supabase
          .rpc('queue_reprocess_job', {
            p_item_id: item.id,
            p_provider: provider
          });
        
        if (jobError) {
          console.warn('Failed to queue processing job:', jobError);
          // Don't throw - item was created, processing can be retried
        } else {
          console.log('✅ Processing job queued:', jobData);
          
          // Trigger worker immediately for instant processing
          try {
            await fetch('https://xlhjevmymaexbhaebkuk.supabase.co/functions/v1/swirl-ingestion-worker', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-worker-secret': 'swirl-worker-secret-2025'
              },
              body: JSON.stringify({})
            });
          } catch (triggerError) {
            console.warn('Worker trigger failed (item still queued):', triggerError);
          }
        }
      } catch (queueError) {
        console.warn('Queue error (non-fatal):', queueError);
      }

      console.log('✅ Item captured:', { 
        itemId: item.id, 
        sourceType: itemData.source_type 
      });
      
      return item;
    } catch (error) {
      console.error('Failed to capture item:', error);
      throw error;
    }
  }

  /**
   * Track an ingestion event
   * This is called AFTER creating an item in deepsearch.items
   * 
   * @deprecated Use captureItem() instead for new code
   */
  async trackIngestion(userId, sourceType, itemId, meta = {}) {
    try {
      // ✅ IMPORTANT: ingestion_events is in public schema, not deepsearch
      const { data, error } = await supabase
        .schema('public')
        .from('ingestion_events')
        .insert({
          user_id: userId,
          source_type: sourceType,
          item_id: itemId,
          meta
        })
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ Ingestion tracked:', { sourceType, itemId });
      return data;
    } catch (error) {
      console.error('Failed to track ingestion:', error);
      throw error;
    }
  }

  /**
   * Ingest a web clip from Chrome extension
   */
  async ingestWebClip(userId, clipData) {
    try {
      // Auto-detect content type from URL
      const detectedType = this.detectWebContentType(
        clipData.url || clipData.pageUrl, 
        clipData
      );
      
      // Create item using captureItem helper
      const item = await this.captureItem(
        userId,
        {
          source_type: detectedType,
          url: clipData.url,
          image_url: clipData.image_url || clipData.thumbnail_url,
          title: clipData.title,
          description: clipData.description || clipData.text,
          raw_text: clipData.text || clipData.content,
          meta: {
            ...clipData.meta,
            source: 'chrome_extension',
            captured_from: clipData.pageUrl || clipData.url,
            detected_type: detectedType,
            // Product-specific metadata
            ...(detectedType === 'product' && {
              product: {
                price: clipData.price,
                brand: clipData.brand,
                rating: clipData.rating,
                availability: clipData.availability
              }
            }),
            // Book-specific metadata
            ...(detectedType === 'book' && {
              book: {
                author: clipData.author,
                isbn: clipData.isbn,
                publisher: clipData.publisher,
                publish_date: clipData.publishDate,
                cover_url: clipData.coverUrl || clipData.image_url
              }
            }),
            // Video-specific metadata
            ...((detectedType === 'youtube' || detectedType === 'video') && {
              video: {
                duration: clipData.duration,
                channel: clipData.channel,
                views: clipData.views,
                upload_date: clipData.uploadDate
              }
            })
          }
        },
        {
          extension_version: clipData.version,
          page_url: clipData.pageUrl,
          content_type: detectedType
        }
      );

      return item;
    } catch (error) {
      console.error('Failed to ingest web clip:', error);
      throw error;
    }
  }

  /**
   * Ingest a file upload (drag & drop)
   */
  async ingestUpload(userId, file, onProgress) {
    try {
      console.log('📤 [IngestionService] Starting upload:', {
        userId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // 1. Create upload tracking entry
      console.log('1️⃣ [IngestionService] Creating upload tracking...');
      const uploadTracking = await this.createUploadTracking(userId, file);
      console.log('✅ [IngestionService] Upload tracking created:', uploadTracking.id);

      // 2. Upload file to Supabase storage
      // IMPORTANT: Path must include userId as first folder for RLS policies
      const storagePath = `${userId}/${uploadTracking.id}/${file.name}`;
      console.log('2️⃣ [IngestionService] Uploading to storage:', storagePath);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('items')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: (progress) => {
            console.log('📊 [IngestionService] Upload progress:', progress);
            if (onProgress) {
              const percentage = (progress.loaded / progress.total) * 100;
              onProgress(percentage);
            }
          }
        });

      if (uploadError) {
        console.error('❌ [IngestionService] Storage upload failed:', uploadError);
        throw uploadError;
      }
      console.log('✅ [IngestionService] Storage upload successful:', uploadData);

      // 3. Get public URL
      console.log('3️⃣ [IngestionService] Getting public URL...');
      const { data: { publicUrl } } = supabase.storage
        .from('items')
        .getPublicUrl(storagePath);
      console.log('✅ [IngestionService] Public URL:', publicUrl);

      // 4. Detect source type from file
      const sourceType = this.getSourceTypeFromMime(file.type, file.name);
      console.log('4️⃣ [IngestionService] Detected source type:', sourceType);
      
      // 5. Create item using captureItem helper
      console.log('5️⃣ [IngestionService] Creating item in deepsearch.items...');
      const item = await this.captureItem(
        userId,
        {
          source_type: sourceType,
          url: publicUrl,
          image_url: ['image', 'svg', 'gif', 'screenshot'].includes(sourceType) ? publicUrl : null,
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension from title
          description: this.generateFileDescription(file, sourceType),
          meta: {
            filename: file.name,
            filesize: file.size,
            mimetype: file.type,
            storage_path: storagePath,
            source: 'drag_drop',
            file_extension: file.name.split('.').pop()
          }
        },
        {
          filename: file.name,
          filesize: file.size,
          mimetype: file.type,
          detected_type: sourceType
        }
      );
      console.log('✅ [IngestionService] Item created:', item);

      // 6. Update upload tracking
      console.log('6️⃣ [IngestionService] Updating upload tracking to processing...');
      await this.updateUploadTracking(uploadTracking.id, {
        item_id: item.id,
        status: 'processing',
        storage_path: storagePath
      });

      // 8. Mark upload as completed
      console.log('7️⃣ [IngestionService] Marking upload as completed...');
      await this.updateUploadTracking(uploadTracking.id, {
        status: 'completed'
      });

      console.log('🎉 [IngestionService] Upload completed successfully!');
      return item;
    } catch (error) {
      console.error('❌ [IngestionService] Upload failed:', error);
      console.error('❌ [IngestionService] Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Generate user-friendly description for uploaded files
   */
  generateFileDescription(file, sourceType) {
    const sizeStr = this.formatFileSize(file.size);
    
    const typeDescriptions = {
      'image': `Image file (${sizeStr})`,
      'svg': `Vector graphic (${sizeStr})`,
      'gif': `Animated image (${sizeStr})`,
      'screenshot': `Screenshot (${sizeStr})`,
      'video': `Video file (${sizeStr})`,
      'audio': `Audio file (${sizeStr})`,
      'pdf': `PDF document (${sizeStr})`,
      'ebook': `E-book (${sizeStr})`,
      'document': `Document (${sizeStr})`,
      'spreadsheet': `Spreadsheet (${sizeStr})`,
      'presentation': `Presentation (${sizeStr})`
    };
    
    return typeDescriptions[sourceType] || `Uploaded file (${sizeStr})`;
  }

  /**
   * Format file size
   */
  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  }

  /**
   * Ingest a note
   */
  async ingestNote(userId, noteData) {
    try {
      // Create item using captureItem helper
      const item = await this.captureItem(
        userId,
        {
          source_type: 'note',
          title: noteData.title || 'Untitled Note',
          description: noteData.content,
          raw_text: noteData.content,
          meta: {
            source: 'user_created',
            ...noteData.meta
          }
        },
        {
          note_length: noteData.content?.length || 0
        }
      );

      return item;
    } catch (error) {
      console.error('Failed to ingest note:', error);
      throw error;
    }
  }

  /**
   * Ingest a link
   */
  async ingestLink(userId, linkData) {
    try {
      // Create item using captureItem helper
      const item = await this.captureItem(
        userId,
        {
          source_type: 'link',
          url: linkData.url,
          title: linkData.title || linkData.url,
          description: linkData.description,
          image_url: linkData.image_url,
          meta: {
            source: 'manual_save',
            ...linkData.meta
          }
        },
        {
          url: linkData.url
        }
      );

      return item;
    } catch (error) {
      console.error('Failed to ingest link:', error);
      throw error;
    }
  }

  /**
   * Get recent ingestions (unified feed)
   */
  async getRecentIngestions(limit = 50, offset = 0, sourceType = null) {
    try {
      const { data, error } = await supabase.rpc('get_recent_ingestions', {
        p_limit: limit,
        p_offset: offset,
        p_source_type: sourceType
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get recent ingestions:', error);
      return [];
    }
  }

  /**
   * Get unified feed view
   */
  async getUnifiedFeed(limit = 50) {
    try {
      // ✅ IMPORTANT: unified_feed view is in public schema, not deepsearch
      const { data, error } = await supabase
        .schema('public')
        .from('unified_feed')
        .select('*')
        .order('captured_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get unified feed:', error);
      return [];
    }
  }

  // ============================================================================
  // Helper methods
  // ============================================================================

  /**
   * Get the appropriate processing provider for a source type
   */
  getProviderForSourceType(sourceType) {
    const videoTypes = ['video', 'youtube', 'tiktok', 'instagram'];
    const imageTypes = ['image', 'svg', 'gif', 'screenshot', 'photo'];
    
    if (videoTypes.includes(sourceType)) {
      return 'gettranscribe'; // Video transcription
    }
    if (imageTypes.includes(sourceType)) {
      return 'image'; // Image OCR + analysis
    }
    return 'generic'; // Text embedding only
  }

  async createUploadTracking(userId, file) {
    // ✅ IMPORTANT: upload_tracking is in public schema, not deepsearch
    const { data, error } = await supabase
      .schema('public')
      .from('upload_tracking')
      .insert({
        user_id: userId,
        filename: file.name,
        filesize: file.size,
        mimetype: file.type,
        status: 'uploading'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateUploadTracking(trackingId, updates) {
    // ✅ IMPORTANT: upload_tracking is in public schema, not deepsearch
    const { error } = await supabase
      .schema('public')
      .from('upload_tracking')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', trackingId);

    if (error) throw error;
  }

  /**
   * Detect source type from MIME type and filename
   */
  getSourceTypeFromMime(mimeType, filename = '') {
    const ext = filename.toLowerCase().split('.').pop();
    
    // Images
    if (mimeType.startsWith('image/svg')) return 'svg';
    if (mimeType.startsWith('image/gif')) return 'gif';
    if (mimeType.startsWith('image/')) return 'image';
    
    // Videos
    if (mimeType.startsWith('video/')) return 'video';
    
    // Audio
    if (mimeType.startsWith('audio/')) return 'audio';
    
    // Documents
    if (mimeType === 'application/pdf' || ext === 'pdf') return 'pdf';
    
    // Ebooks
    if (ext === 'epub' || ext === 'mobi' || ext === 'azw' || ext === 'azw3') return 'ebook';
    
    // Office documents
    if (mimeType.includes('word') || ext === 'doc' || ext === 'docx') return 'document';
    if (mimeType.includes('spreadsheet') || ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'spreadsheet';
    if (mimeType.includes('presentation') || ext === 'ppt' || ext === 'pptx') return 'presentation';
    
    // Text files
    if (mimeType === 'text/plain' || ext === 'txt' || ext === 'md') return 'document';
    
    // Default
    return 'upload';
  }

  /**
   * Detect content type from URL patterns (for Chrome extension)
   */
  detectWebContentType(url, pageData = {}) {
    const urlLower = url.toLowerCase();
    
    // Books & Reading (check BEFORE e-commerce to catch Amazon books)
    if (urlLower.includes('goodreads.com') || 
        urlLower.includes('books.google.com') ||
        (urlLower.includes('amazon.com') && urlLower.includes('/dp/') && this.isBookUrl(url))) {
      return 'book';
    }
    
    // E-commerce platforms (products)
    if (urlLower.includes('amazon.com') || 
        urlLower.includes('shopify.com') ||
        urlLower.includes('etsy.com') ||
        urlLower.includes('ebay.com')) {
      return 'product';
    }
    
    // Social media
    if (urlLower.includes('instagram.com')) return 'instagram';
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return 'youtube';
    if (urlLower.includes('linkedin.com')) return 'linkedin';
    if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) return 'twitter';
    if (urlLower.includes('tiktok.com')) return 'tiktok';
    
    // Articles & Blogs
    if (urlLower.includes('medium.com') ||
        urlLower.includes('substack.com') ||
        urlLower.includes('/blog/') ||
        urlLower.includes('/article/')) {
      return 'article';
    }
    
    // Check if it's highlighted text
    if (pageData.isSelection || pageData.selectedText) {
      return 'highlighted_text';
    }
    
    // Default to web_clip
    return 'web_clip';
  }
  
  /**
   * Helper to distinguish books from products on Amazon
   * Books typically have:
   * - /dp/ in URL (ISBN/ASIN)
   * - Categories like "Books", "Kindle", "Textbooks"
   * - ISBN-10 or ISBN-13 format ASINs (10 characters starting with digits)
   */
  isBookUrl(url) {
    const urlLower = url.toLowerCase();
    
    // Check for book-specific paths
    if (urlLower.includes('/gp/product/') || urlLower.includes('/gp/aw/d/')) {
      return false; // These are usually general products
    }
    
    // Extract ASIN from /dp/ URL
    const dpMatch = url.match(/\/dp\/([A-Z0-9]{10})/i);
    if (dpMatch) {
      const asin = dpMatch[1];
      // Books usually have ASINs starting with digits (ISBN format)
      // Most electronics/products have mixed alphanumeric ASINs like B08N5WRWNW
      if (/^\d/.test(asin)) {
        return true; // Likely a book (ISBN-10 format)
      }
      // Check if it's an ASIN starting with 'B' (usually products)
      if (asin.startsWith('B')) {
        return false; // Definitely a product
      }
    }
    
    // Check URL for book keywords
    if (urlLower.includes('book') || 
        urlLower.includes('kindle') ||
        urlLower.includes('textbook') ||
        urlLower.includes('/books/')) {
      return true;
    }
    
    // Default: if it has /dp/ and we can't determine, assume book
    return urlLower.includes('/dp/');
  }
}

export default new IngestionService();
